function sandlotColorGroups(variants, colorIndex, choices, values) {
  const groups = {regular: {values: [], prices: []}, sale: {values: [], prices: []}};
  for (const value of values) {
    const matching = variants.filter(variant => variant.options[colorIndex] === value);
    const exact = matching.filter(variant => choices.every((choice,index) => index === colorIndex || !choice || variant.options[index] === choice));
    const candidates = exact.length ? exact : matching;
    const available = candidates.filter(variant => variant.available);
    const pool = available.length ? available : candidates;
    const sorted = [...pool].sort((a,b) => a.priceCents - b.priceCents);
    const representative = sorted[0];
    if (!representative) continue;
    const group = groups[representative.compare ? 'sale' : 'regular'];
    group.values.push(value);
    group.prices.push(...pool);
  }
  for (const group of Object.values(groups)) {
    group.prices.sort((a,b) => a.priceCents - b.priceCents);
    group.price = group.prices[0]?.price || '';
    group.varies = group.prices.length > 0 && group.prices[0].priceCents !== group.prices.at(-1).priceCents;
  }
  return groups;
}

class SandlotProduct extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.variants = JSON.parse(this.querySelector('[data-product-variants]').textContent);
    this.fields = [...this.querySelectorAll('[data-option-index]')];
    this.photos = [...this.querySelectorAll('[data-photo-id]')];
    this.colorSwatches = [...this.querySelectorAll('[data-color-value]')];
    this.form = this.querySelector('form');
    this.add = this.querySelector('[name="add"]');
    this.quantity = this.querySelector('[name="quantity"]');
    this.status = this.querySelector('[data-product-status]');
    this.querySelector('noscript')?.remove();
    const size = this.querySelector('[data-size-option]');
    if (size && this.dataset.explicitVariant !== 'true') {
      size.querySelectorAll('input').forEach(input => input.checked = false);
    }
    this.fields.forEach(field => field.addEventListener('change', () => this.update(true)));
    this.photos.forEach(photo => photo.addEventListener('click', () => {
      const color = this.querySelector('[data-color-option]');
      const variant = this.variants.find(variant => variant.imageId === Number(photo.dataset.photoId));
      if (color && variant) {
        const value = variant.options[Number(color.dataset.optionIndex)];
        const input = [...color.querySelectorAll('input')].find(input => input.value === value);
        if (input) { input.checked = true; this.update(true); }
      }
      this.showPhoto(photo);
    }));
    this.querySelectorAll('[data-photo-step]').forEach(button => button.addEventListener('click', () => {
      const index = this.photos.findIndex(photo => photo.getAttribute('aria-pressed') === 'true');
      this.showPhoto(this.photos[(index + Number(button.dataset.photoStep) + this.photos.length) % this.photos.length]);
    }));
    let touchStart;
    this.querySelector('.sg-pdp__photo').addEventListener('touchstart', event => { touchStart = event.changedTouches[0].clientX; }, {passive:true});
    this.querySelector('.sg-pdp__photo').addEventListener('touchend', event => {
      const delta = event.changedTouches[0].clientX - touchStart;
      if (Math.abs(delta) > 50) this.querySelector(`[data-photo-step="${delta < 0 ? 1 : -1}"]`)?.click();
    }, {passive:true});
    this.querySelectorAll('[data-quantity-step]').forEach(button => button.addEventListener('click', () => {
      const min = Number(this.quantity.min) || 1;
      const max = this.quantity.max ? Number(this.quantity.max) : Infinity;
      this.quantity.value = Math.max(min, Math.min(max, Number(this.quantity.value) + Number(button.dataset.quantityStep) * Number(this.quantity.step)));
    }));
    this.form.addEventListener('submit', event => {
      if (!this.current?.available) { event.preventDefault(); return; }
      if (location.hostname === '127.0.0.1' || location.hostname === 'localhost') {
        event.preventDefault();
        this.status.textContent = this.dataset.previewLabel;
      }
    });
    this.update(false);
  }
  update(changeUrl) {
    const choices = this.fields.map(field => field.querySelector('input:checked')?.value);
    this.fields.forEach((field,index) => {
      field.querySelectorAll('input').forEach(input => {
        const matching = this.variants.filter(variant => variant.options[index] === input.value && choices.every((choice,other) => other >= index || !choice || variant.options[other] === choice));
        input.disabled = !field.hasAttribute('data-color-option') && !matching.some(variant => variant.available);
        input.closest('label').classList.toggle('is-unavailable', input.disabled);
      });
      const checked = field.querySelector('input:checked');
      if (checked?.disabled && field.hasAttribute('data-size-option') && changeUrl) checked.checked = false;
    });
    const selected = this.fields.map(field => field.querySelector('input:checked')?.value);
    this.fields.forEach((field,index) => field.querySelector('[data-option-label]').textContent = selected[index] || '—');
    this.updateColorGroups(selected);
    const variant = this.fields.length ? this.variants.find(variant => variant.options.every((value,index) => value === selected[index])) : this.variants[0];
    const photoVariant = variant || this.variants.find(variant => selected.every((value,index) => !value || variant.options[index] === value));
    this.current = variant;
    this.add.disabled = !variant?.available;
    this.add.textContent = !selected.every(Boolean) ? this.dataset.selectLabel : !variant ? this.dataset.unavailableLabel : variant.available ? this.dataset.addLabel : this.dataset.soldLabel;
    const id = this.querySelector('[data-variant-id]');
    id.name = 'id';
    id.value = variant?.id || '';
    id.disabled = !variant;
    if (photoVariant) {
      this.querySelector('[data-price]').textContent = photoVariant.price;
      const compare = this.querySelector('[data-compare-price]');
      compare.textContent = photoVariant.compare;
      compare.hidden = !photoVariant.compare;
      if (changeUrl) {
        const photo = this.photos.find(photo => Number(photo.dataset.photoId) === photoVariant.imageId);
        if (photo) this.showPhoto(photo);
      }
    }
    if (variant) {
      this.quantity.min = variant.min;
      this.quantity.step = variant.step;
      if (variant.max) this.quantity.max = variant.max;
      else this.quantity.removeAttribute('max');
      this.quantity.value = Math.max(variant.min, Math.min(variant.max || Infinity, variant.min + Math.floor((Number(this.quantity.value) - variant.min) / variant.step) * variant.step));
    }
    if (changeUrl) {
      this.status.textContent = '';
      const url = new URL(location.href);
      if (variant) url.searchParams.set('variant', variant.id);
      else url.searchParams.delete('variant');
      history.replaceState({}, '', url);
    }
    this.updatePhotoCount();
    this.dispatchEvent(new CustomEvent('sandlot:fit-size-change',{bubbles:true,detail:{size:this.querySelector('[data-size-option] input:checked')?.value,variantId:variant?.id,options:selected}}));
  }
  updateColorGroups(choices) {
    const field = this.querySelector('[data-color-option]');
    const wrapper = field?.querySelector('.sg-pdp__swatch-groups');
    if (!wrapper) return;
    const groups = sandlotColorGroups(this.variants, Number(field.dataset.optionIndex), choices, this.colorSwatches.map(label => label.dataset.colorValue));
    const focused = document.activeElement;
    for (const [name,group] of Object.entries(groups)) {
      const container = wrapper.querySelector(`[data-swatch-group="${name}"]`);
      const list = container.querySelector('[data-swatch-list]');
      for (const [index,value] of group.values.entries()) {
        const label = this.colorSwatches.find(label => label.dataset.colorValue === value);
        if (list.children[index] !== label) list.insertBefore(label, list.children[index] || null);
      }
      container.hidden = !group.values.length;
      container.querySelector('[data-swatch-price]').textContent = group.varies ? wrapper.dataset.fromPrice.replace('__PRICE__', group.price) : group.price;
    }
    if (focused && field.contains(focused) && document.activeElement !== focused) focused.focus({preventScroll:true});
  }
  showPhoto(photo) {
    if (!photo) return;
    const image = this.querySelector('.sg-pdp__main-image');
    image.removeAttribute('srcset');
    image.src = photo.dataset.photoSrc;
    image.alt = photo.dataset.photoAlt;
    this.photos.forEach(item => item.setAttribute('aria-pressed', String(item === photo)));
    const strip = photo.parentElement;
    strip.scrollTo({left:photo.offsetLeft - strip.offsetLeft - strip.clientWidth / 2 + photo.clientWidth / 2,behavior:'instant'});
    this.updatePhotoCount();
  }
  updatePhotoCount() {
    const count = this.querySelector('[data-photo-count]');
    if (count) count.textContent = `${this.photos.findIndex(photo => photo.getAttribute('aria-pressed') === 'true') + 1} / ${this.photos.length}`;
  }
}
if (!customElements.get('sandlot-product')) customElements.define('sandlot-product', SandlotProduct);
